<script setup lang="ts">
const { t: $t } = useI18n()

import { CommentVisibility } from '~/prisma/enums'
import type { UpdateComment, UpdateOrCreateComment } from '~/types'

const { openCommentCreateOrUpdatePopup: { data: popupData, close: closePopup } } = useGlobalOpeners()

const { comment: commentQuery } = useQuery()
const { data: comment } = commentQuery.byId(computed(() => popupData.value?.mode === 'update' ? popupData.value.commentId : undefined))
const { data: originalComment } = commentQuery.byId(computed(() => popupData.value?.mode === 'create' && popupData.value.originalCommentId ? popupData.value.originalCommentId : undefined))

const payload = computed((): UpdateOrCreateComment | undefined => {
  if (!popupData.value) {
    return undefined
  }

  if (popupData.value.mode === 'create' && (!popupData.value.originalCommentId || originalComment.value)) {
    const visibleToIds = new Set<string>()
    if (originalComment.value && originalComment.value.visibleTo.length > 0) {
      originalComment.value.visibleTo.forEach(({ id }) => visibleToIds.add(id))
    }

    return {
      mode: 'create',
      data: {
        type: popupData.value.type,
        visibility: CommentVisibility.Private,
        visibleToIds: [...visibleToIds],
        originalCommentId: popupData.value.originalCommentId,
        subject: originalComment.value?.subject,
        text: '',
        files: [],
        [commentConfigs[popupData.value.type].commentTableRelationFieldName]: popupData.value.id,
      },
    }
  }

  if (comment.value) {
    return {
      mode: 'update',
      data: {
        ...comment.value,
        visibleToIds: comment.value.visibleTo.map(user => user.id),
      },
    } satisfies UpdateComment
  }
})

const type = computed(() => payload.value?.data.type)
const entityId = computed(() => {
  if (!payload.value) {
    return undefined
  }
  return payload.value.data[commentConfigs[payload.value.data.type].commentTableRelationFieldName] ?? undefined
})
const { userOptions, isSupportRestrictedToSpecificUser } = useCommentVisibleToOptions(type, entityId)

const isReply = computed(() => !!(payload.value?.mode === 'create'
  ? payload.value.data.originalCommentId
  : comment.value?.originalCommentId))

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const create = useMutation({
  mutationFn: $trpc.comment.create.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('comment.notification.create.error') }),
  onSuccess: async () => {
    const invalidateQueriesPromises = []

    invalidateQueriesPromises.push(queryClient.invalidateQueries({ queryKey: ['comment'] }))
    invalidateQueriesPromises.push(queryClient.invalidateQueries({
      queryKey: popupData.value?.type ? [commentConfigs[popupData.value.type].entity] : undefined,
    }))

    popupData.value?.refreshQueries?.forEach((query) => {
      invalidateQueriesPromises.push(queryClient.invalidateQueries({ queryKey: [commentConfigs[query].entity] }))
    })

    await Promise.all(invalidateQueriesPromises)

    closePopup()
    notification.success({ title: $t('comment.notification.create.success'), duration: 4500 })
  },
})

const update = useMutation({
  mutationFn: $trpc.comment.update.mutate,
  onError: makeTrpcErrorToast(notification, { description: 'Der Kommentar konnte nicht aktualisiert werden' }),
  onSuccess: async () => {
    const invalidateQueriesPromises = []

    invalidateQueriesPromises.push(queryClient.invalidateQueries({ queryKey: ['comment'] }))
    invalidateQueriesPromises.push(queryClient.invalidateQueries({
      queryKey: popupData.value?.type ? [commentConfigs[popupData.value.type].entity] : undefined,
    }))
    popupData.value?.refreshQueries?.forEach((query) => {
      invalidateQueriesPromises.push(queryClient.invalidateQueries({ queryKey: [commentConfigs[query].entity] }))
    })

    await Promise.all(invalidateQueriesPromises)

    closePopup()
    notification.success({ title: 'Der Kommentar wurde erfolgreich aktualisiert', duration: 4500 })
  },
})

function save(payload: UpdateOrCreateComment) {
  if (payload.mode === 'create') {
    return create.mutateAsync(payload.data)
  } else {
    return update.mutateAsync(payload.data)
  }
}

function manualClosePopup() {
  if (popupData.value?.isClosable) {
    closePopup()
  }
}
</script>

<template>
  <ThePopup
    v-if="payload && popupData"
    :show="Boolean(payload)"
    :title="`Kommentar ${payload.mode === 'create' ? 'Erstellung' : 'Bearbeiten'}`"
    :is-spinning="create.isPending.value || update.isPending.value"
    @close="manualClosePopup"
  >
    <CommentCreateOrEditForm
      v-if="popupData"
      :payload="payload"
      :is-visibility-adjustable="popupData.isVisibilityAdjustable"
      :is-close-disabled="!popupData.isClosable"
      :is-reply
      :user-options
      :is-support-restricted-to-specific-user
      @save="save"
      @close="manualClosePopup"
    />
  </ThePopup>
</template>
